import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Car,
  Calendar,
  CalendarDays,
  Timer,
  User,
  Settings,
  Wrench,
  Clock,
  AlertTriangle,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { useVehicles, useDeleteVehicle } from "@/hooks/useVehicles";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { VehicleMaintenanceSection } from "@/components/vehicles/VehicleMaintenanceSection";
import { VehicleTimeline } from "@/components/vehicles/VehicleTimeline";
import { VehicleIssuesSection } from "@/components/vehicles/VehicleIssuesSection";
import { VehicleEventsSection } from "@/components/vehicles/VehicleEventsSection";
import { VehicleSessionsSection } from "@/components/vehicles/VehicleSessionsSection";
import { VehicleModal } from "@/components/vehicles/VehicleModal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import PageLoading from "@/components/common/PageLoading";
import PageErrorState from "@/components/common/PageErrorState";
import { useVehicleTimelineInfinite } from "@/hooks/useVehicleTimeline";
import { useTranslation } from "react-i18next";
import { formatUserName } from "@/utils/userName";
import type { InfiniteData } from "@tanstack/react-query";
import type { VehicleTimelineEvent } from "@/types";

const VEHICLE_TABS = [
  "timeline",
  "maintenance",
  "issues",
  "sessions",
  "events",
] as const;

type VehicleTab = (typeof VEHICLE_TABS)[number];

function isVehicleTab(value: string | null): value is VehicleTab {
  return !!value && (VEHICLE_TABS as readonly string[]).includes(value);
}

export default function VehicleDetailPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const activeTab: VehicleTab = isVehicleTab(tabParam) ? tabParam : "timeline";
  const setActiveTab = (tab: VehicleTab) => {
    setSearchParams(
      (prev) => {
        prev.set("tab", tab);
        return prev;
      },
      { replace: true },
    );
  };

  const [isVehicleEditModalOpen, setIsVehicleEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { user } = useAuth();
  const { data: vehicles, isLoading, error } = useVehicles();
  const deleteVehicle = useDeleteVehicle();
  const {
    data: timelinePages,
    isLoading: isLoadingTimeline,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVehicleTimelineInfinite(vehicleId || "");

  const eventsData = timelinePages as
    | InfiniteData<{ events: VehicleTimelineEvent[]; total: number }, number>
    | undefined;
  const timelineEvents = eventsData?.pages.flatMap((p) => p.events) ?? [];
  const vehicle = vehicles?.find((v) => v.id === vehicleId);
  usePageMeta({
    title: vehicle
      ? (vehicle.name ?? `${vehicle.year} ${vehicle.maker} ${vehicle.model}`)
      : undefined,
    description: vehicle?.description,
  });

  if (!vehicleId) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="text-lg text-red-600">{t("pages:vehicles.detail.invalidId")}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageLoading />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageErrorState
          icon={Car}
          message={t("pages:vehicles.detail.loadFailed")}
          details={t("pages:vehicles.detail.noAccessMessage")}
          retryLabel={t("pages:vehicles.detail.backToVehicles")}
          onRetry={() => navigate("/vehicles")}
        />
      </div>
    );
  }

  const vehicleDisplayName =
    vehicle.name ||
    `${vehicle.year} ${vehicle.maker} ${vehicle.model}`;

  const vehicleSubtitle = vehicle.name
    ? `${vehicle.year} ${vehicle.maker} ${vehicle.model}`
    : vehicle.description || t("pages:vehicles.detail.noDescription");

  return (
 <div className="max-w-7xl mx-auto">
      {/* Header */}
 <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
 <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/vehicles")}
 className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
          >
 <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
 <h1 className="page-title">
              {vehicleDisplayName}
            </h1>
 <p className="page-subtitle">{vehicleSubtitle}</p>
          </div>
        </div>

      <div className="flex flex-wrap gap-2">
          {user?.id === vehicle.userId && (
            <>
              <button
                onClick={() => setIsVehicleEditModalOpen(true)}
                className="btn-secondary px-4 py-2 flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>{t("pages:vehicles.detail.sections.editVehicle")}</span>
              </button>
              <button
                onClick={() => setIsDeleteConfirmOpen(true)}
                className="btn-secondary px-4 py-2 flex items-center space-x-2 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                <span>{t("common:delete")}</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Vehicle Info */}
      <div className="bg-white rounded-lg border border-secondary-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">
          {t("pages:vehicles.detail.information")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <Car className="h-5 w-5 text-secondary-400" />
            <div>
              <div className="text-sm text-secondary-500">{t("pages:vehicles.detail.fields.makeAndModel")}</div>
              <div className="font-medium text-secondary-900">
                {vehicle.maker} {vehicle.model}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-secondary-400" />
            <div>
              <div className="text-sm text-secondary-500">{t("pages:vehicles.detail.fields.year")}</div>
              <div className="font-medium text-secondary-900">
                {vehicle.year}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <User className="h-5 w-5 text-secondary-400" />
            <div>
              <div className="text-sm text-secondary-500">{t("pages:vehicles.detail.fields.owner")}</div>
              <div className="font-medium text-secondary-900">
                {vehicle.user
                  ? formatUserName(vehicle.user)
                  : t("pages:vehicles.detail.fields.unknownOwner")}
              </div>
            </div>
          </div>
        </div>

        {vehicle.description && (
          <div className="mt-4 pt-4 border-t border-secondary-100">
            <div className="text-sm text-secondary-500 mb-2">{t("common:description")}</div>
            <p className="text-secondary-700 bg-secondary-50 rounded p-3">
              {vehicle.description}
            </p>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-secondary-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-secondary-500">
            <div>
              <span className="font-medium">{t("pages:vehicles.detail.created")}</span>{" "}
              {new Date(vehicle.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
            {vehicle.updatedAt && vehicle.updatedAt !== vehicle.createdAt && (
              <div>
                <span className="font-medium">{t("pages:vehicles.detail.updated")}</span>{" "}
                {new Date(vehicle.updatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div role="tablist" className="mb-6 flex flex-wrap border-b border-secondary-200">
        {(
          [
            {
              id: "timeline",
              label: t("pages:vehicles.detail.sections.timeline"),
              icon: Clock,
            },
            {
              id: "maintenance",
              label: t("pages:vehicles.detail.sections.maintenance"),
              icon: Wrench,
            },
            {
              id: "issues",
              label: t("pages:vehicles.detail.sections.issues"),
              icon: AlertTriangle,
            },
            {
              id: "sessions",
              label: t("pages:vehicles.detail.sections.sessions"),
              icon: Timer,
            },
            {
              id: "events",
              label: t("pages:vehicles.detail.sections.events"),
              icon: CalendarDays,
            },
          ] satisfies { id: VehicleTab; label: string; icon: LucideIcon }[]
        ).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            onClick={() => setActiveTab(id)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === id
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-secondary-600 hover:text-secondary-900"
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === "events" && (
        <div className="mb-8">
          <VehicleEventsSection vehicleId={vehicleId} />
        </div>
      )}

      {activeTab === "sessions" && (
        <div className="mb-8">
          <VehicleSessionsSection vehicleId={vehicleId} />
        </div>
      )}

      {activeTab === "issues" && (
 <div className="mb-8">
 <VehicleIssuesSection vehicle={vehicle} className="" />
        </div>
      )}

      {activeTab === "timeline" && (
 <div className="mb-8">
 <div className="bg-white rounded-lg border border-secondary-200 p-6">
 <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-secondary-900">
                {t("pages:vehicles.detail.timelineTitle")}
              </h2>
              <div className="text-sm text-secondary-500">
                {timelineEvents && timelineEvents.length > 0 && (
                  <span>{t("pages:vehicles.detail.eventsCount", { count: timelineEvents.length })}</span>
                )}
              </div>
            </div>
            <VehicleTimeline
              events={timelineEvents}
              vehicle={vehicle}
              isLoading={isLoadingTimeline && timelineEvents.length === 0}
              onLoadMore={() => fetchNextPage()}
              hasMore={!!hasNextPage}
              isFetchingMore={isFetchingNextPage}
            />
          </div>
        </div>
      )}

      {activeTab === "maintenance" && (
 <div className="mb-8">
 <VehicleMaintenanceSection vehicle={vehicle} className="" />
        </div>
      )}

      {/* Vehicle Edit Modal */}
      <VehicleModal
        isOpen={isVehicleEditModalOpen}
        onClose={() => setIsVehicleEditModalOpen(false)}
        vehicle={vehicle}
        mode="edit"
      />

      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={async () => {
          try {
            await deleteVehicle.mutateAsync(vehicle.id);
            navigate("/vehicles");
          } catch {
            // useDeleteVehicle owns the error toast; keep the modal open.
          }
        }}
        title={t("pages:vehicles.deleteTitle")}
        message={t("pages:vehicles.deleteConfirmation", {
          name: vehicleDisplayName,
        })}
        confirmButtonText={t("common:delete")}
        confirmButtonColor="red"
        isLoading={deleteVehicle.isPending}
      />
    </div>
  );
}
